import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { YapService } from '../services/api';
import { useTheme } from './ThemeProvider';
import { yapCharLimit } from '../constants/posts';
import { useUserData } from './UserDataContext';

/**
 * Edit a yap or a reply, inside the 15-minute window.
 *
 * One component for both because the difference is one id: YapService.editPost
 * resolves a yap across yaps/forum_posts/tribe_posts, and a reply is addressed
 * by its parent plus its own id.
 *
 * Shows the time remaining and closes itself when the window runs out. A form
 * that silently stops working when you press Save is worse than one that told
 * you the clock was running — and the clock can expire while the modal is open,
 * since somebody can sit on a half-finished edit for longer than they have.
 */

interface Props {
  open: boolean;
  onClose: () => void;
  /** The yap, or the yap the reply belongs to. */
  postId: string;
  /** Present when editing a reply rather than the post itself. */
  replyId?: string;
  /** Where the post lives, when already known. Skips the lookup. */
  collectionName?: string;
  initialContent: string;
  /** Creation time, for the countdown. */
  timestamp: any;
  onSaved?: (newContent: string) => void;
}


export function EditPostModal({
  open, onClose, postId, replyId, collectionName, initialContent, timestamp, onSaved,
}: Props) {
  const { theme } = useTheme();
  const { userProfile } = useUserData();
  const charLimit = yapCharLimit(userProfile);
  const [content, setContent] = useState(initialContent);
  const [saving, setSaving] = useState(false);
  const [remaining, setRemaining] = useState(() => YapService.editWindowRemaining(timestamp));

  useEffect(() => {
    if (open) setContent(initialContent);
  }, [open, initialContent]);

  // The window can close while this is open. Ticking here means the button
  // disables and the modal closes on its own, rather than the save failing.
  useEffect(() => {
    if (!open) return;
    const tick = () => {
      const left = YapService.editWindowRemaining(timestamp);
      setRemaining(left);
      if (left <= 0) {
        toast.info('The 15 minute window to edit this has passed.');
        onClose();
      }
    };
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [open, timestamp, onClose]);

  const dark = theme === 'dark';
  const changed = content.trim() !== initialContent.trim();
  const empty = content.trim().length === 0;
  const tooLong = charLimit !== null && content.length > charLimit;

  const mins = Math.floor(remaining / 60000);
  const secs = Math.floor((remaining % 60000) / 1000);

  const save = async () => {
    if (saving || empty || tooLong || !changed) return;
    setSaving(true);
    try {
      await YapService.editPost(postId, content, { replyId, collectionName });
      onSaved?.(content.trim());
      toast.success('Edited');
      onClose();
    } catch (e: any) {
      toast.error(e?.message || 'Could not save that edit.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 flex items-center justify-center p-4"
          style={{ zIndex: 9999, background: 'rgba(0,0,0,0.6)' }}
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.96, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.96, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full rounded-2xl p-4"
            style={{
              maxWidth: 520,
              backgroundColor: dark ? '#1a1a1a' : '#fdfcf0',
              border: `1px solid ${dark ? '#333' : '#e5e5e5'}`,
            }}
          >
            <div className="flex items-center justify-between mb-3">
              <p style={{ fontSize: 15, fontWeight: 800, color: dark ? '#fff' : '#111' }}>
                {replyId ? 'Edit reply' : 'Edit yap'}
              </p>
              <button onClick={onClose} aria-label="Close" style={{ color: dark ? '#9ca3af' : '#6b7280' }}>
                <X className="w-5 h-5" />
              </button>
            </div>

            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows={5}
              autoFocus
              className="w-full rounded-xl px-3 py-2 outline-none resize-none"
              style={{
                fontSize: 15,
                lineHeight: 1.5,
                color: dark ? '#fff' : '#111',
                backgroundColor: dark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
                // Inline, matching the chat bubbles: a pasted link has to wrap
                // rather than push the field wider than the modal.
                overflowWrap: 'anywhere',
              }}
            />

            <div className="flex items-center justify-between mt-2">
              <span style={{ fontSize: 11, color: dark ? '#9ca3af' : '#6b7280' }}>
                {mins}:{String(secs).padStart(2, '0')} left to edit
              </span>
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: tooLong ? '#E74C3C' : (dark ? '#9ca3af' : '#6b7280'),
                }}
              >
                {charLimit === null ? content.length : `${content.length}/${charLimit}`}
              </span>
            </div>

            <div className="flex gap-2 mt-4">
              <button
                onClick={onClose}
                className="flex-1 py-2.5 rounded-xl"
                style={{
                  fontSize: 14, fontWeight: 700,
                  color: dark ? '#fff' : '#111',
                  backgroundColor: dark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)',
                }}
              >
                Cancel
              </button>
              <button
                onClick={save}
                disabled={saving || empty || tooLong || !changed}
                className="flex-1 py-2.5 rounded-xl flex items-center justify-center gap-2 disabled:opacity-50"
                style={{
                  fontSize: 14, fontWeight: 700, color: '#fff',
                  background: 'linear-gradient(90deg, #4478ea, #2d1ef1)',
                }}
              >
                {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                {saving ? 'Saving' : 'Save'}
              </button>
            </div>

            {/* Said plainly, because it is the thing people ask about. */}
            <p className="mt-3" style={{ fontSize: 10, color: dark ? '#9ca3af' : '#6b7280' }}>
              Edited posts are marked as edited.
            </p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
